/* global window */
import { SelectionActionType } from '@engoo/realtime-protocol/selection';
import { getSelectionRange, findSelectedNodesIndices } from 'app/components/call/SharedSelection/selection';
import LazySortedNodes, { INodePoint } from 'app/components/call/SharedSelection/LazySortedNodes';
import SharedSelectionRender from 'app/components/call/SharedSelection/SharedSelectionRender';
import SharedSelectionScreenReaderText from 'app/components/call/SharedSelection/SharedSelectionScreenReaderText';
import { KeyPath } from 'app/models/entityStateSync/keypath';
import { ISelectionAreaContext, ParticipantSelections } from 'app/models/rtc/selection/constants';
import { animationFrameDebounce as globalAnimationFrameDebounce } from 'app/utils/async';
import { bindDomEvents, unbindDomEvents } from 'app/utils/DomEvents';
import { Map } from 'immutable';
import React, { createContext, FunctionComponent, useState, useMemo, useEffect, useRef, useContext } from 'react';
import { createStyles } from 'app/utils/css';
import { useCallerColor } from 'app/components/call/SharedSelection/colors';
import { ISharedSelectionDocumentContext, useSharedSelectionContext } from 'app/components/call/SharedSelectionDocumentContext';

const SharedSelectionAreaContext = createContext<ISelectionAreaContext | undefined>(undefined);

type SharedSelectionAreaUpdater = ReturnType<ISharedSelectionDocumentContext['addArea']>;

/**
 * Undefine the value of the context to disable any nested shared
 * selection components.
 */
export const UndefineSharedSelectionAreaContext: FunctionComponent = ({ children }) =>
  <SharedSelectionAreaContext.Provider value={undefined}>
    {children}
  </SharedSelectionAreaContext.Provider>;

export interface ISharedSelectionAreaDeps {
  readonly callerColor?: string;
  readonly isDocumentActive: boolean;
  readonly addRemoteSharedSelectionArea: () => SharedSelectionAreaUpdater;
  readonly animationFrameDebounce: typeof globalAnimationFrameDebounce;
  readonly selections: ParticipantSelections;
}

export const SharedSelectionArea: FunctionComponent<ISharedSelectionAreaDeps> = ({
  callerColor,
  isDocumentActive,
  addRemoteSharedSelectionArea,
  animationFrameDebounce,
  selections,
  children,
}) => {
  const sortedNodes = useRef(new LazySortedNodes());

  const [nodesIndex, setNodesIndex] = useState<Map<KeyPath, INodePoint>>(Map());

  // Group required dependencies so that we don't need to `!`
  // callerColor if we only had something like:
  // `isActive = isDocumentActive && callerColor`
  const deps = useMemo(
    () => isDocumentActive && callerColor
      ? { isDocumentActive, callerColor }
      : null,
    [isDocumentActive, callerColor],
  );

  const areaContext = useMemo(() => {
    const updateIndex = () => {
      setNodesIndex(sortedNodes.current.getIndex());
    };

    return {
      addDiscoveryNode: (path: KeyPath, node: HTMLElement, block: boolean) => {
        sortedNodes.current.add({ path, node, block });
        updateIndex();
      },
      removeDiscoveryNode: (path: KeyPath) => {
        sortedNodes.current.remove(path);
        updateIndex();
      },
    };
  }, [setNodesIndex]);

  useEffect(() => {
    if (!deps) {
      return;
    }

    const updater = addRemoteSharedSelectionArea();

    // Resolves current selection to either `null` (if empty) or set of indices
    const getCurrentSelection = () => {
      const selection = getSelectionRange(window.getSelection());

      if (!selection) {
        return null;
      }

      const nodes = sortedNodes.current.getNodes();
      const indices = selection && findSelectedNodesIndices(selection, nodes);

      if (!indices) {
        return null;
      }

      const { startIndex, startOffset, endIndex, endOffset } = indices;

      // When start and end is the same path and length of the
      // selection is 0 characters (start and end offsets are equal),
      // clear selection
      if (startIndex === endIndex && startOffset === endOffset) {
        return null;
      }

      return {
        startIndex,
        startOffset,
        endIndex,
        endOffset,
        cursorPosition: selection.cursorPosition,
      };
    };

    const onSelectionChange = animationFrameDebounce(() => {
      const selection = getCurrentSelection();

      if (!selection) {
        updater.updateRemote({
          type: SelectionActionType.Clear,
        });
      } else {
        const nodes = sortedNodes.current.getNodes();
        const { startIndex, startOffset, endIndex, endOffset } = selection;

        updater.updateRemote({
          type: SelectionActionType.Set,
          color: deps.callerColor,
          paths: nodes.slice(startIndex, endIndex + 1).map(({ path }) => path.toArray()),
          startOffset,
          endOffset,
          cursorPosition: selection.cursorPosition,
        });
      }
    });

    const documentEvents = {
      selectionchange: onSelectionChange,
    };

    bindDomEvents(window.document, documentEvents);
    onSelectionChange();

    return () => {
      onSelectionChange.cancel();
      unbindDomEvents(window.document, documentEvents);
      updater.unregisterArea();
    };
  }, [deps, addRemoteSharedSelectionArea, animationFrameDebounce]);

  return (
    <SharedSelectionAreaContext.Provider value={deps ? areaContext : undefined}>
      <Styles>{css =>
        /*
         * Render a div with this class to cancel out native
         * selection look when shared selection is active.
         */
        <div className={deps ? css('sharedSelectionArea') : ''}>
          <SharedSelectionRender
            selections={selections}
            nodes={nodesIndex}
          >
            {children}
          </SharedSelectionRender>
          <SharedSelectionScreenReaderText
            selections={selections}
            nodes={nodesIndex}
          />
        </div>
      }</Styles>
    </SharedSelectionAreaContext.Provider>
  );
};


const SharedSelectionAreaInjectDependencies: FunctionComponent = (props) => {
  const callerColor = useCallerColor();
  const { selections, addArea, isBlocked } = useSharedSelectionContext();

  return (
    <SharedSelectionArea
      callerColor={callerColor}
      isDocumentActive={!isBlocked}
      addRemoteSharedSelectionArea={addArea}
      animationFrameDebounce={globalAnimationFrameDebounce}
      selections={selections}
      {...props}
    />
  );
};

export default SharedSelectionAreaInjectDependencies;

export const useSharedSelectionArea = (path: KeyPath, block: boolean) => {
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const area = useContext(SharedSelectionAreaContext);

  useEffect(() => {
    if (!ref || !area) {
      return;
    }

    area.addDiscoveryNode(path, ref, block);

    return () => {
      area.removeDiscoveryNode(path);
    };
  }, [ref, area, block, path]);

  return { setRef };
};

export const restoreNativeSelectionStyle = () => ({
  '& ::-moz-selection': {
    backgroundColor: 'highlight',
  },
  '& ::selection': {
    backgroundColor: 'highlight',
  },
});

export const nativeSelectionStyles = {
  sharedSelectionArea: {
    // Reset styles for all children.
    '& ::-moz-selection': {
      backgroundColor: 'transparent',
    },

    // Don't collapse this one with previous one. It won't work given how CSS
    // selector work.
    '& ::selection': {
      backgroundColor: 'transparent',
    },
  },
};

const Styles = createStyles(nativeSelectionStyles);
